import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { convertLocalToUtcTime } from 'common/utils/date';
import { Alert, Box, Chip, DialogContent, Divider, Tooltip, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import HourSelectFieldAdapter from 'components/shared/field-adapters/hour-picker-field-adapter/HourPickerFieldAdapter';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import TimeFieldAdapter from 'components/shared/field-adapters/TimeFieldAdapter';
import TrainerSelectFieldAdapter from 'components/shared/field-adapters/TrainerSelectFieldAdapter';
import SessionCreateDialogActions from 'components/session/dialog/create/SessionCreateDialogActions';
import StyledSessionDialogCreateForm from 'common/theme/dialog/create-session/StyledSessionDialogCreateForm';
import StyledSessionFieldsSectionTitle from 'common/theme/session/fields/StyledSessionFieldsSectionTitle';
import { getLanguageOptions } from 'common/constants/options';
import { userAvatarWithSpecialtiesFields } from 'common/constants/query-fields/user';
import groupSessionSlice from 'store/modules/groupSession';
import trainerSlice from 'store/modules/trainer';

const ACTIVE_PLUS_VALIDATION = gql`
  query ActivePlusValidation {
    allPlans {
      id
      name
      active_plus_clients
      session_types {
        id
        name
      }
    }
  }
`;

// Chequeo por categoría seleccionada: ¿hay entrenadores para esta especialidad específica?
const CHECK_CATEGORY_TRAINERS = gql`
  query CheckCategoryTrainers($specialties: [Int!]) {
    availableTrainers(specialties: $specialties) {
      id
    }
  }
`;

const initialValues = {
  session_type_id: '',
  language: '',
  date: null,
  from: null,
  to: null,
  trainer_id: '',
  recurrence_weeks: 4,
  observations: ''
};

const validate = (values) => {
  const errors = {};
  if (!values.session_type_id) errors.session_type_id = 'Requerido';
  if (!values.language) errors.language = 'Requerido';
  if (!values.date) errors.date = 'Requerido';
  if (!values.from) errors.from = 'Requerido';
  if (!values.to) errors.to = 'Requerido';
  if (!values.trainer_id) errors.trainer_id = 'Requerido';
  if (!values.recurrence_weeks || values.recurrence_weeks < 1) errors.recurrence_weeks = 'Mínimo 1';
  if (values.recurrence_weeks > 52) errors.recurrence_weeks = 'Máximo 52';
  return errors;
};

function GroupSessionCreateDialogContent({ onDialogClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['labels', 'sessions']);
  const formRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const sessionTypes = useSelector((state) => state.sessionType.sessionTypeOptions);
  const trainers = useSelector((state) => state.trainer.data.trainers);
  const trainerStatus = useSelector((state) => state.trainer.status.general);
  const availableTimeSlots = useSelector((state) => state.trainer.availableTimeSlots?.data || []);
  const slotsStatus = useSelector((state) => state.trainer.availableTimeSlots?.status);

  const { data: validationData } = useQuery(ACTIVE_PLUS_VALIDATION, {
    fetchPolicy: 'network-only'
  });

  const activePlusPlan =
    validationData?.allPlans?.find((p) => {
      const normalized = p.name?.toLowerCase().replace(/\s+/g, '') || '';
      return normalized.includes('activeplus');
    }) || null;
  const hasActivePlusUsers = (activePlusPlan?.active_plus_clients ?? 0) > 0;

  const activePlusSpecialtyIds = activePlusPlan?.session_types?.map((st) => parseInt(st.id, 10)) || [];

  // Chequeo por categoría seleccionada
  const [checkCategoryTrainers, { data: categoryCheckData, loading: categoryCheckLoading }] =
    useLazyQuery(CHECK_CATEGORY_TRAINERS, { fetchPolicy: 'network-only' });

  // null = sin categoría / pendiente | true = tiene entrenadores | false = nadie la imparte
  let hasCategoryTrainers = null;
  if (selectedCategoryId) {
    hasCategoryTrainers = categoryCheckLoading
      ? true
      : (categoryCheckData?.availableTrainers?.length ?? 0) > 0;
  }

  const sessionTypeOptions = (activePlusPlan?.session_types ?? sessionTypes).map((st) => ({
    value: parseInt(st.id, 10),
    label: st.name
  }));

  const autoSessionTypeId =
    sessionTypeOptions.length === 1 ? sessionTypeOptions[0].value : '';
  const categoryLocked = sessionTypeOptions.length === 1;
  const formInitialValues = { ...initialValues, session_type_id: autoSessionTypeId };

  // Cuando hay una categoría auto-seleccionada (plan con 1 sola categoría), lanzar el check
  useEffect(() => {
    if (autoSessionTypeId) {
      setSelectedCategoryId(autoSessionTypeId);
      checkCategoryTrainers({ variables: { specialties: [autoSessionTypeId] } });
    }
  }, [autoSessionTypeId, checkCategoryTrainers]);

  // Time options derived from real backend slots (same approach as SessionFieldsSessionDetails)
  const hasAvailableSlots = Array.isArray(availableTimeSlots) && availableTimeSlots.length > 0;
  const timeOptions = useMemo(() => {
    const baseDate = selectedDate || new Date();
    if (!hasAvailableSlots || slotsStatus === 'loading') return [];

    const map = new Map();
    availableTimeSlots.forEach((slot) => {
      if (!slot?.start_time) return;
      try {
        const startTime = new Date(slot.start_time);
        if (Number.isNaN(startTime.getTime())) return;
        const key = `${startTime.getHours()}:${String(startTime.getMinutes()).padStart(2, '0')}`;
        if (!map.has(key)) {
          const d = new Date(baseDate);
          d.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
          map.set(key, {
            value: d,
            label: d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true })
          });
        }
      } catch (e) {
        // ignore invalid slots
      }
    });
    return Array.from(map.values()).sort((a, b) => a.value.getTime() - b.value.getTime());
  }, [selectedDate, availableTimeSlots, hasAvailableSlots, slotsStatus]);


  // Called when date or language changes: fetch available time slots
  const handleDateOrLanguageChange = useCallback(
    (field, value) => {
      const form = formRef.current;
      if (!form) return;

      form.change('from', '');
      form.change('to', '');
      form.change('trainer_id', '');
      dispatch(trainerSlice.actions.clearList());
      dispatch(trainerSlice.actions.clearAvailableTimeSlots());

      const date = field === 'date' ? value : form.getFieldState('date')?.value;
      const lang = field === 'language' ? value : form.getFieldState('language')?.value;
      const sessionTypeId = autoSessionTypeId || form.getFieldState('session_type_id')?.value;

      if (date) setSelectedDate(date);

      if (date && lang && sessionTypeId) {
        dispatch(
          trainerSlice.actions.getAvailableTimeSlots({ date, sessionTypeId, language: lang })
        );
      }
    },
    [dispatch, autoSessionTypeId]
  );

  // Called when "from" is picked: auto-set "to" = from + 1h and fetch trainers for that slot
  const handleFromChange = useCallback(
    (value) => {
      const form = formRef.current;
      if (!form || !value) return;

      const date = form.getFieldState('date')?.value;
      const lang = form.getFieldState('language')?.value;
      if (!date || !lang) return;

      const toTime = new Date(value.getTime() + 60 * 60 * 1000);
      form.change('to', toTime);
      form.change('trainer_id', '');
      dispatch(trainerSlice.actions.clearList());

      const y = date.getFullYear();
      const mo = date.getMonth();
      const d = date.getDate();
      const startDate = convertLocalToUtcTime(new Date(y, mo, d, value.getHours(), value.getMinutes()));
      const endDate = convertLocalToUtcTime(new Date(y, mo, d, toTime.getHours(), toTime.getMinutes()));

      const currentCategoryId = form.getFieldState('session_type_id')?.value;
      const specialtiesFilter = currentCategoryId
        ? [parseInt(currentCategoryId, 10)]
        : activePlusSpecialtyIds;

      dispatch(
        trainerSlice.actions.listAvailable({
          fields: userAvatarWithSpecialtiesFields,
          filters: {
            date_range: { start_date: startDate, end_date: endDate },
            specialties: specialtiesFilter,
            languages: lang
          }
        })
      );
    },
    [dispatch, activePlusSpecialtyIds]
  );

  const onSubmit = (values) => {
    const baseDate = values.date;
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const day = baseDate.getDate();

    const startDate = convertLocalToUtcTime(
      new Date(year, month, day, values.from.getHours(), values.from.getMinutes())
    );
    const endDate = convertLocalToUtcTime(
      new Date(year, month, day, values.to.getHours(), values.to.getMinutes())
    );

    dispatch(
      groupSessionSlice.actions.create({
        trainer_id: parseInt(values.trainer_id, 10),
        session_type_id: parseInt(values.session_type_id, 10),
        language: values.language,
        start_date: startDate,
        end_date: endDate,
        observations: values.observations || null,
        recurrence_weeks: parseInt(values.recurrence_weeks, 10)
      })
    );
    onDialogClose();
  };

  // El formulario solo se bloquea por plan/usuarios faltantes, o si la categoría seleccionada no tiene entrenadores
  const formDisabled = !!validationData && (
    !hasActivePlusUsers || !activePlusPlan || hasCategoryTrainers === false
  );

  let formBlockedTooltip = null;
  if (formDisabled) {
    if (!activePlusPlan) formBlockedTooltip = t('noActivePlusPlan', { ns: 'sessions' });
    else if (!hasActivePlusUsers) formBlockedTooltip = t('noActivePlusUsers', { ns: 'sessions' });
    else formBlockedTooltip = t('noCategoryTrainers', { ns: 'sessions' });
  }

  return (
    <DialogContent>
      {/* Validación 1: no existe plan Active Plus */}
      {validationData && !activePlusPlan && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('noActivePlusPlan', { ns: 'sessions' })}
        </Alert>
      )}

      {/* Validación 2: plan existe pero ningún cliente lo tiene asignado */}
      {validationData && activePlusPlan && !hasActivePlusUsers && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('noActivePlusUsers', { ns: 'sessions' })}
        </Alert>
      )}

      {/* Validación 3: categoría seleccionada pero nadie la puede impartir */}
      {validationData && activePlusPlan && hasActivePlusUsers && hasCategoryTrainers === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('noCategoryTrainers', { ns: 'sessions' })}
        </Alert>
      )}

      {/* Plan detectado — mostrar plan + categorías (read-only) */}
      {activePlusPlan && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 1,
            backgroundColor: 'rgba(255,255,255,0.04)'
          }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {t('activePlusPlanLabel', { ns: 'sessions' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5, mb: 1, fontWeight: 600 }}>
            {activePlusPlan.name}
          </Typography>
          {activePlusPlan.session_types?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {activePlusPlan.session_types.map((st) => (
                <Chip
                  key={st.id}
                  label={st.name}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'text.secondary', borderColor: 'rgba(255,255,255,0.2)' }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Form
        initialValues={formInitialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, form, invalid, pristine, values }) => {
          formRef.current = form;

          // Nadie imparte esta categoría: búsqueda completada sin slots disponibles
          const hasCompletedSearch = slotsStatus === 'success' || slotsStatus === 'error';
          const noCategoryTrainers =
            hasCompletedSearch &&
            !hasAvailableSlots &&
            !!values.date &&
            !!values.language &&
            !!values.session_type_id;

          // Check secundario: entrenador ya seleccionado pero sin la especialidad (defensivo)
          const selectedTrainer = trainers.find((tr) => String(tr.id) === String(values.trainer_id));
          const trainerSpecialtyIds =
            selectedTrainer?.specialties?.map((s) => parseInt(s.id, 10)) || [];
          const sessionTypeId = values.session_type_id
            ? parseInt(values.session_type_id, 10)
            : null;
          const trainerMismatch =
            !!values.trainer_id &&
            !!sessionTypeId &&
            !trainerSpecialtyIds.includes(sessionTypeId);

          // Hora seleccionada pero ningún entrenador AP disponible para ese slot
          const hasCompletedTrainerSearch =
            trainerStatus === 'success' || trainerStatus === 'error';
          const noTrainersForSlot =
            !!values.from &&
            hasCompletedTrainerSearch &&
            trainers.length === 0;

          const showCategoryWarning = noCategoryTrainers || trainerMismatch || noTrainersForSlot;

          return (
            <StyledSessionDialogCreateForm onSubmit={handleSubmit} key={autoSessionTypeId}>
              <div>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('recurrenceInfo', { ns: 'sessions', weeks: values.recurrence_weeks || 4 })}
                </Alert>

                <StyledSessionFieldsSectionTitle component="h4">
                  {t('sessionDetails', { ns: 'labels' })}
                </StyledSessionFieldsSectionTitle>
                <Divider sx={{ marginBottom: '22px' }} />

                {showCategoryWarning && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('noCategoryTrainers', { ns: 'sessions' })}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <Field
                      id="session_type_id"
                      name="session_type_id"
                      component={SelectFieldAdapter}
                      options={sessionTypeOptions}
                      label={t('category', { ns: 'labels' })}
                      fullWidth
                      required
                      disabled={categoryLocked || (formDisabled && (!activePlusPlan || !hasActivePlusUsers))}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Field
                      id="language"
                      name="language"
                      component={SelectFieldAdapter}
                      options={getLanguageOptions(t)}
                      label={t('sessionLanguage', { ns: 'labels' })}
                      fullWidth
                      required
                      disabled={formDisabled}
                    />
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Tooltip
                      title={showCategoryWarning ? t('noCategoryTrainers', { ns: 'sessions' }) : ''}
                      arrow>
                      <span>
                        <Field
                          id="date"
                          name="date"
                          component={DatePickerFieldAdapter}
                          label={t('date', { ns: 'labels' })}
                          disablePast
                          disabled={trainerMismatch}
                          renderInputProps={{ fullWidth: true, required: true }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid xs={6} md={4}>
                    <Tooltip
                      title={noCategoryTrainers ? t('noCategoryTrainers', { ns: 'sessions' }) : ''}
                      arrow>
                      <span>
                        <Field
                          id="from"
                          name="from"
                          component={HourSelectFieldAdapter}
                          label={t('from', { ns: 'common' })}
                          dateFormat="hh aaa"
                          disablePast
                          options={timeOptions}
                          referenceDate={values.date || new Date()}
                          disabled={formDisabled || !hasAvailableSlots || slotsStatus === 'loading' || !values.date || !values.language}
                          renderInputProps={{ fullWidth: true, required: true }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid xs={6} md={4}>
                    <Tooltip
                      title={noCategoryTrainers ? t('noCategoryTrainers', { ns: 'sessions' }) : ''}
                      arrow>
                      <span>
                        <Field
                          id="to"
                          name="to"
                          component={TimeFieldAdapter}
                          label={t('to', { ns: 'common' })}
                          dateFormat="hh aaa"
                          referenceDate={values.date}
                          renderInputProps={{ fullWidth: true, required: true, disabled: formDisabled || noCategoryTrainers || !values.from }}
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Field
                      id="recurrence_weeks"
                      name="recurrence_weeks"
                      component={TextFieldAdapter}
                      label={t('recurrenceWeeks', { ns: 'labels' })}
                      type="number"
                      inputProps={{ min: 1, max: 52 }}
                      fullWidth
                      required
                      disabled={formDisabled}
                    />
                  </Grid>
                </Grid>

                <StyledSessionFieldsSectionTitle component="h4" sx={{ mt: 2 }}>
                  {t('trainer', { count: 1, ns: 'labels' })}
                </StyledSessionFieldsSectionTitle>
                <Divider sx={{ marginBottom: '22px' }} />

                <Field
                  id="trainer_id"
                  name="trainer_id"
                  component={TrainerSelectFieldAdapter}
                  label={t('trainer', { count: 1, ns: 'labels' })}
                  status={trainerStatus}
                  trainers={trainers}
                  disabled={formDisabled}
                  emptyMessage={t('trainerNotAvailable', { ns: 'sessions' })}
                />

                <StyledSessionFieldsSectionTitle component="h4" sx={{ mt: 2 }}>
                  {t('observations', { ns: 'labels' })}
                </StyledSessionFieldsSectionTitle>
                <Divider sx={{ marginBottom: '22px' }} />

                <Field
                  id="observations"
                  name="observations"
                  component={TextFieldAdapter}
                  label={t('observations', { ns: 'labels' })}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={formDisabled}
                />
              </div>

              <SessionCreateDialogActions
                disabledTooltip={formBlockedTooltip}
                invalid={invalid}
                onDialogClose={onDialogClose}
                pristine={pristine}
              />

              {/* Listeners: fetch slots on date/language change, fetch trainers on from change */}
              <OnChange name="date">
                {(value) => handleDateOrLanguageChange('date', value)}
              </OnChange>
              <OnChange name="language">
                {(value) => handleDateOrLanguageChange('language', value)}
              </OnChange>
              <OnChange name="from">
                {(value) => handleFromChange(value)}
              </OnChange>
              {/* Resetear fecha/hora/entrenador y lanzar chequeo de entrenadores por categoría */}
              <OnChange name="session_type_id">
                {(value) => {
                  form.change('date', null);
                  form.change('from', '');
                  form.change('to', '');
                  form.change('trainer_id', '');
                  dispatch(trainerSlice.actions.clearList());
                  dispatch(trainerSlice.actions.clearAvailableTimeSlots());
                  const numId = value ? parseInt(value, 10) : null;
                  setSelectedCategoryId(numId);
                  if (numId) checkCategoryTrainers({ variables: { specialties: [numId] } });
                }}
              </OnChange>
            </StyledSessionDialogCreateForm>
          );
        }}
      />
    </DialogContent>
  );
}

GroupSessionCreateDialogContent.propTypes = {
  onDialogClose: PropTypes.func.isRequired
};

export default GroupSessionCreateDialogContent;
